import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { fmtDate, fmtTime } from "@/lib/format";
import dayjs from "dayjs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, CalendarIcon, Video } from "lucide-react";

const DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

export default function CalendarPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [cursor, setCursor] = useState(dayjs());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", start: "", end: "", type: "meeting" });

  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: async () => { const d = (await api.get("/calendar-events")).data; return Array.isArray(d) ? d : []; } });
  const { data: tasks = [] } = useQuery({ queryKey: ["tasks"], queryFn: async () => { const d = (await api.get("/tasks")).data; return Array.isArray(d) ? d : []; } });

  const items = [
    ...events.map((e) => ({ id: e.id, title: e.title, date: e.start, color: e.color || "#4f46e5", kind: "event", video_room: e.video_room || null })),
    ...tasks.filter((t) => t.due_date).map((t) => ({ id: t.id, title: t.title, date: t.due_date, color: "#f59e0b", kind: "task" })),
  ];

  const start = cursor.startOf("month").startOf("week");
  const cells = Array.from({ length: 42 }).map((_, i) => start.add(i, "day"));

  const create = async () => {
    if (!form.title || !form.start) { toast.error("Başlık ve tarih gerekli"); return; }
    try {
      await api.post("/calendar-events", { ...form, start: dayjs(form.start).toISOString(), end: form.end ? dayjs(form.end).toISOString() : dayjs(form.start).add(1, "hour").toISOString() });
      toast.success("Etkinlik oluşturuldu");
      qc.invalidateQueries({ queryKey: ["events"] });
      setOpen(false); setForm({ title: "", start: "", end: "", type: "meeting" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const upcoming = items.filter((i) => dayjs(i.date).isAfter(dayjs().subtract(1, "day"))).sort((a, b) => dayjs(a.date) - dayjs(b.date));

  return (
    <div>
      <PageHeader title="Takvim" subtitle="Etkinlikler ve teslim tarihleri"
        actions={<Button onClick={() => setOpen(true)} data-testid="new-event-button"><Plus className="h-4 w-4 mr-1.5" /> Etkinlik</Button>} />

      <Tabs defaultValue="month">
        <TabsList>
          <TabsTrigger value="month" data-testid="cal-tab-month">Ay</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="cal-tab-agenda">Ajanda</TabsTrigger>
        </TabsList>

        <TabsContent value="month" className="mt-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold font-display capitalize">{cursor.format("MMMM YYYY")}</h3>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" onClick={() => setCursor(cursor.subtract(1, "month"))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setCursor(dayjs())}>Bugün</Button>
                <Button variant="outline" size="icon" onClick={() => setCursor(cursor.add(1, "month"))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">{DAYS.map((d) => <div key={d} className="text-xs text-muted-foreground text-center font-medium py-1">{d}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((c, i) => {
                const dayItems = items.filter((it) => dayjs(it.date).isSame(c, "day"));
                const isToday = c.isSame(dayjs(), "day");
                const otherMonth = !c.isSame(cursor, "month");
                return (
                  <div key={i} className={`min-h-[84px] rounded-lg border p-1 ${otherMonth ? "bg-muted/30 opacity-50" : "bg-card"} ${isToday ? "ring-2 ring-indigo-500" : ""}`}>
                    <div className="text-xs font-medium text-right px-1">{c.date()}</div>
                    <div className="space-y-0.5 mt-0.5">
                      {dayItems.slice(0, 3).map((it) => (
                        it.video_room ? (
                          <button key={it.id} onClick={() => nav(`/video?room=${it.video_room}`)} title="Görüntülü toplantıya katıl"
                            className="w-full text-left text-[10px] truncate px-1 py-0.5 rounded text-white flex items-center gap-1 hover:opacity-90" style={{ background: it.color }} data-testid={`cal-video-${it.id}`}>
                            <Video className="h-2.5 w-2.5 shrink-0" />{it.title}
                          </button>
                        ) : (
                          <div key={it.id} className="text-[10px] truncate px-1 py-0.5 rounded text-white" style={{ background: it.color }}>{it.title}</div>
                        )
                      ))}
                      {dayItems.length > 3 && <div className="text-[9px] text-muted-foreground px-1">+{dayItems.length - 3}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="agenda" className="mt-4">
          <Card className="divide-y">
            {upcoming.length ? upcoming.map((it) => (
              <div key={it.id} className="flex items-center gap-3 p-3">
                <div className="h-9 w-9 rounded-lg grid place-items-center text-white text-xs" style={{ background: it.color }}><CalendarIcon className="h-4 w-4" /></div>
                <div className="flex-1"><p className="text-sm font-medium">{it.title}</p><p className="text-xs text-muted-foreground">{fmtDate(it.date)} {fmtTime(it.date)}</p></div>
                {it.video_room ? (
                  <Button size="sm" className="h-8" onClick={() => nav(`/video?room=${it.video_room}`)} data-testid={`cal-join-${it.id}`}><Video className="h-4 w-4 mr-1.5" /> Katıl</Button>
                ) : (
                  <Badge variant="outline" className="text-[10px]">{it.kind === "task" ? "Görev" : "Etkinlik"}</Badge>
                )}
              </div>
            )) : <EmptyState icon={CalendarIcon} title="Yaklaşan etkinlik yok" />}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader><DialogTitle>Yeni Etkinlik</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="event-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlangıç</Label><Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1.5" data-testid="event-start-input" /></div>
              <div><Label>Bitiş</Label><Input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button onClick={create} data-testid="event-save-button">Oluştur</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
